import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import MobileTabSelect from './MobileTabSelect';
import ShowArchiveCard, { ShowArchiveCardList, cardControl, joinLocation, formatShowDateLong } from './ShowArchiveCard';
import RelationshipCard, { RelationshipCardList, capacityLabel } from './RelationshipCard';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';
import DeletionRequestModal from './DeletionRequestModal';
import ActionsDropdown from './ActionsDropdown';
import FlyerHistoryTabs, { MultiEventFlyerCard, S as FlyerS, Smobile as FlyerSmobile, FLYER_PAGE_SIZE, FLYER_COLS } from './FlyerHistoryTabs';
import { ZoomHint, ImageLightbox, ImageQuickEditModal } from './ZoomableImage';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// (2026-10-15) Photos moved from the header-right info tabs to the body tabs,
// right of Flyers (series and standalone/child festivals alike).
const INFO_TABS = ['Show Stats', 'Bio', 'Links'];
const PAGE_SIZE = 25;

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

const formatDateShort = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const formatDateLong = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const formatRequestedAt = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ── Pagination ──────────────────────────────────────────────────────────────
// A direct "jump to page" control (screenshot reference: Adidas.com's site-wide
// pagination) instead of a Prev/Next + numbered-button row. A native <select>
// gives the scrollable page list and click-to-jump behavior for free.
function Pagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={styles.pageJumpWrap}>
            <span style={styles.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={styles.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={styles.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Concurrency-limited fetch with retry (SeriesFlyerHistoryTab) ───────────────
// A festival series with many child festivals previously fired one GET
// /images request per child SIMULTANEOUSLY via Promise.all, each wrapped in
// a bare `.catch(() => [])`. At scale (10 children × 45-50 events each) any
// transient failure on any one request — Lambda cold start under burst
// concurrency, RDS connection pressure against the shared pg Pool (max: 2
// per instance), or throttling — silently dropped that child's flyers with
// no indication, explaining "flyers missing, but a refresh fixes it."
// Fixed two ways: (1) cap how many requests are in flight at once instead
// of firing all of them together, and (2) retry a failed request a couple
// of times with a short backoff before giving up on that child.
const FLYER_FETCH_CONCURRENCY = 3;
const FLYER_FETCH_RETRIES = 2; // additional attempts after the first — 3 tries total

async function fetchChildFestivalFlyers(cf, token) {
    let lastError = null;
    for (let attempt = 0; attempt <= FLYER_FETCH_RETRIES; attempt++) {
        try {
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'festival', entityId: cf.id, mode: 'flyer-calendar', sort: 'desc' },
                headers: { Authorization: token },
            });
            return { images: (res.data.images || []).map(img => ({ ...img, _childFestival: cf })), failed: false };
        } catch (err) {
            lastError = err;
            if (attempt < FLYER_FETCH_RETRIES) {
                // Short, increasing backoff gives a transient condition (cold
                // start / DB connection pressure) a moment to clear instead
                // of retrying straight back into the same burst.
                await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
            }
        }
    }
    console.error(`Failed to load flyers for child festival ${cf.id} after ${FLYER_FETCH_RETRIES + 1} attempts:`, lastError);
    return { images: [], failed: true };
}

// Runs `worker` over `items` with at most `limit` in flight at once — a
// small worker-pool rather than a fixed-size Promise.all, so the next item
// starts as soon as any slot frees up.
async function runWithConcurrencyLimit(items, limit, worker) {
    const results = new Array(items.length);
    let nextIndex = 0;
    async function runNext() {
        while (nextIndex < items.length) {
            const current = nextIndex++;
            results[current] = await worker(items[current], current);
        }
    }
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
    return results;
}

// ── Flyers tab content for a Festival Series ──────────────────────────────────
// A series always has a parent relationship to its children — its own Flyers
// tab wouldn't be useful with the standard three sub-tabs (Show Flyer History
// and Shows Without Flyers would always be empty, since a series never has
// its own events). Instead, this flattens each child festival's own Festival
// Flyer History images into one grid, tagged with which child they belong to.
// Unchanged from the pre-redesign FestivalDetail.js — just moved to the last
// body-tab position (spec: SHARC_FestivalDetail_Redesign_Spec_2026-09-10 §4).
function SeriesFlyerHistoryTab({ childFestivals }) {
    const isMobile = useIsMobile();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [failedCount, setFailedCount] = useState(0);
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);

    useEffect(() => {
        let cancelled = false;
        async function loadAll() {
            if (!childFestivals || childFestivals.length === 0) {
                setImages([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            setError('');
            setFailedCount(0);
            try {
                const token = await getIdToken();
                const perChild = await runWithConcurrencyLimit(
                    childFestivals,
                    FLYER_FETCH_CONCURRENCY,
                    (cf) => fetchChildFestivalFlyers(cf, token)
                );
                if (!cancelled) {
                    setImages(perChild.flatMap(r => r.images));
                    setFailedCount(perChild.filter(r => r.failed).length);
                }
            } catch {
                if (!cancelled) setError('Failed to load flyer images.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        loadAll();
        return () => { cancelled = true; };
    }, [childFestivals]);

    const latestDate = (img) => {
        const dates = (img.linked_events || []).map(e => e.event_date).filter(Boolean).sort();
        return dates[dates.length - 1] || '';
    };
    const sorted = [...images].sort((a, b) => {
        const cmp = latestDate(a) < latestDate(b) ? -1 : latestDate(a) > latestDate(b) ? 1 : 0;
        return sortDir === 'desc' ? -cmp : cmp;
    });

    const paginated = sorted.slice((page - 1) * FLYER_PAGE_SIZE, page * FLYER_PAGE_SIZE);
    const remainder = paginated.length % FLYER_COLS;
    const padCount = remainder === 0 ? 0 : FLYER_COLS - remainder;

    if (loading) return <div style={FlyerS.emptyTab}>Loading…</div>;
    if (error)   return <div style={FlyerS.emptyTab}>{error}</div>;

    if (sorted.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>🖼️</div>
                <div style={{ color: '#888', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No Festival Flyers Yet</div>
                <div style={{ color: '#555', fontSize: '14px' }}>Festival Flyer History images from child festivals will appear here once uploaded.</div>
                {failedCount > 0 && (
                    <div style={{ marginTop: '16px', display: 'inline-block', backgroundColor: '#2c1e0d', border: '1px solid #7a5200', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#e0b060' }}>
                        Flyers for {failedCount} of {childFestivals.length} child festival{childFestivals.length !== 1 ? 's' : ''} couldn't be loaded after retrying — this may be why nothing is showing. Try refreshing the page.
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {failedCount > 0 && (
                <div style={{ backgroundColor: '#2c1e0d', border: '1px solid #7a5200', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#e0b060' }}>
                    Flyers for {failedCount} of {childFestivals.length} child festival{childFestivals.length !== 1 ? 's' : ''} couldn't be loaded after retrying. Try refreshing the page.
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={FlyerS.flyerSortGroup}>
                    <label style={FlyerS.flyerSortLabel}>Sort by</label>
                    <select value={sortDir} onChange={e => { setSortDir(e.target.value); setPage(1); }} style={FlyerS.flyerSortSelect}>
                        <option value="desc">Date (Most Recent to Oldest)</option>
                        <option value="asc">Date (Oldest to Most Recent)</option>
                    </select>
                </div>
            </div>
            <div style={isMobile ? { ...FlyerS.flyerGrid, ...FlyerSmobile.flyerGrid } : FlyerS.flyerGrid}>
                {paginated.map(img => (
                    <MultiEventFlyerCard
                        key={img.id}
                        image={img}
                        viewFestivalTo={`/festivals/${img._childFestival.slug || img._childFestival.id}`}
                        festivalLabel={img._childFestival.name}
                    />
                ))}
                {Array.from({ length: padCount }).map((_, i) => <div key={`pad-${i}`} style={FlyerS.flyerCellPad} />)}
            </div>
            <Pagination currentPage={page} totalItems={sorted.length} onPageChange={p => setPage(p)} pageSize={FLYER_PAGE_SIZE} />
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#555' }}>
                {sorted.length} image{sorted.length !== 1 ? 's' : ''}
                {Math.ceil(sorted.length / FLYER_PAGE_SIZE) > 1 ? ` · Page ${page} of ${Math.ceil(sorted.length / FLYER_PAGE_SIZE)}` : ''}
            </div>
        </div>
    );
}

// ── Photos Tab (body tab, right of Flyers — moved from the header-right info tabs 2026-10-15) ──────
// Copied from VenueDetail.js's PhotosTab one-for-one (grid, set/remove
// default, + Add Photo, lightbox, quick-edit modal), entityType swapped to
// 'festival'. This replaces the old standalone collapsible Photos section.
function PhotosTab({ festivalId, festivalName, defaultImageId, onDefaultChanged }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingDefault, setSettingDefault] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [hoveredThumb, setHoveredThumb] = useState(null);
    const [editingImage, setEditingImage] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [removingDefault, setRemovingDefault] = useState(false);

    const loadPhotos = useCallback(async () => {
        if (!festivalId) return;
        setLoading(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'festival', entityId: festivalId },
                headers: { Authorization: token },
            });
            const imgs = res.data.images || [];
            setImages(imgs);
            setError('');
            if (defaultImageId) {
                const defaultImg = imgs.find(i => i.id === defaultImageId);
                if (defaultImg) onDefaultChanged(defaultImg.id, defaultImg.image_url);
            }
        } catch {
            setError('Failed to load photos.');
        } finally {
            setLoading(false);
        }
    }, [festivalId, defaultImageId, onDefaultChanged]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    const handleUploaded = (img) => {
        setImages(prev => [img, ...prev]);
        setShowUpload(false);
        // Backend may have auto-set this upload as the festival's default —
        // sync that up to the parent immediately rather than waiting on a
        // refetch (createImage.js reports this via becameDefault).
        if (img.becameDefault) {
            onDefaultChanged(img.id, img.image_url);
        }
    };

    const handleRemoveDefault = async () => {
        setRemovingDefault(true);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'festival', entityId: festivalId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(null, null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        } finally {
            setRemovingDefault(false);
        }
    };

    const handleSetDefault = async (image) => {
        setSettingDefault(image.id);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'festival', entityId: festivalId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(image.id, image.image_url);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        } finally {
            setSettingDefault(null);
        }
    };

    const handleImageSaved = (updated) => {
        setImages(prev => prev.map(i => i.id === updated.id ? { ...i, ...updated } : i));
        setLightbox(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
    };

    const handleImageDeleted = (id) => {
        setImages(prev => prev.filter(i => i.id !== id));
        setLightbox(null); // image no longer exists — close the lightbox behind it
    };

    if (loading) return <div style={styles.emptyInfo}>Loading photos…</div>;
    if (error) return <div style={styles.emptyInfo}>{error}</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    {defaultImageId && (
                        <button
                            onClick={handleRemoveDefault}
                            disabled={removingDefault}
                            style={{ background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', opacity: removingDefault ? 0.6 : 1 }}
                        >
                            {removingDefault ? 'Removing…' : '✕ Remove Default'}
                        </button>
                    )}
                </div>
                <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                    + Add Photo
                </button>
            </div>

            {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>No photos yet for this festival.</div>
                </div>
            ) : (
                <div style={styles.photoGrid}>
                    {images.map(img => {
                        const isDefault = img.id === defaultImageId;
                        return (
                            <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? styles.photoCardDefault : {}) }}>
                                <div style={styles.photoThumb} onClick={() => setLightbox(img)} onMouseEnter={() => setHoveredThumb(img.id)} onMouseLeave={() => setHoveredThumb(null)}>
                                    <img src={img.image_url} alt={img.caption || festivalName} style={styles.photoThumbImg} loading="lazy" />
                                    {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                    {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                    <ZoomHint visible={hoveredThumb === img.id} />
                                </div>
                                <div style={styles.photoCardBody}>
                                    {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                    {!isDefault && img.is_public && (
                                        <button onClick={() => handleSetDefault(img)} disabled={settingDefault === img.id} style={styles.setDefaultBtn}>
                                            {settingDefault === img.id ? 'Setting…' : '★ Set as Default'}
                                        </button>
                                    )}
                                    {isDefault && <div style={styles.isDefaultLabel}>Current default photo</div>}
                                    {!img.is_public && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Private — only visible to you</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {lightbox && (
                <ImageLightbox
                    src={lightbox.image_url}
                    alt={lightbox.caption || ''}
                    caption={lightbox.caption}
                    onClose={() => setLightbox(null)}
                    onEdit={() => setEditingImage(lightbox)}
                />
            )}

            {editingImage && (
                <ImageQuickEditModal
                    image={editingImage}
                    onClose={() => setEditingImage(null)}
                    onSaved={handleImageSaved}
                    onDeleted={handleImageDeleted}
                />
            )}

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                    presetEntityType="festival"
                    presetEntityId={festivalId}
                    presetEntityName={festivalName}
                />
            )}
        </div>
    );
}

// ── Artists Hosted Tab (body tab — Section 4 of the redesign spec) ─────────────
// Mirrors VenueDetail.js's ArtistsHostedTab exactly. `artists` is already
// scoped server-side (own events for a standalone/child festival, summed
// across all child festivals for a series) — no branching needed here.
function ArtistsHostedTab({ artists, isSeries }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();

    if (!artists || artists.length === 0) return <div style={styles.emptyTab}>No artist data recorded yet.</div>;

    const q = normalizeStr(search);
    const filtered = search ? artists.filter(a =>
        [a.name, a.artist_type, a.artist_style, a.city, a.state, a.country]
            .some(val => val && normalizeStr(val).includes(q))
    ) : artists;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by artist, type, style, city, state, country..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No artists match your search.</div>
            ) : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(artist => (
                                <RelationshipCard
                                    key={artist.id}
                                    title={artist.name}
                                    titleTo={`/artists/${artist.slug || artist.id}`}
                                    lines={[
                                        [artist.artist_type, artist.artist_style].filter(Boolean).join(' · '),
                                        joinLocation(artist.city, artist.state, artist.country),
                                    ]}
                                    counts={[
                                        { value: `${artist.appearances}x`, label: 'Appearances' },
                                        isSeries && { value: `${artist.festival_count}x`, label: '# of Festivals' },
                                    ]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Artist</th>
                                        <th style={styles.th}>Artist Type</th>
                                        <th style={styles.th}>Artist Style</th>
                                        <th style={styles.th}>City</th>
                                        <th style={styles.th}>State/Region</th>
                                        <th style={styles.th}>Country</th>
                                        <th style={styles.th}>Appearances</th>
                                        {/* Only meaningful for a series — for a standalone/child
                                            festival every artist's events share one festival_id,
                                            so this would trivially always read 1x. */}
                                        {isSeries && <th style={styles.th}># of Festivals</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((artist, idx) => (
                                        <tr key={artist.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}>
                                                <Link to={`/artists/${artist.slug || artist.id}`} style={styles.tableLink}>{artist.name}</Link>
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{artist.artist_type || '—'}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{artist.artist_style || '—'}</td>
                                            <td style={styles.td}>{artist.city || '—'}</td>
                                            <td style={styles.td}>{artist.state || '—'}</td>
                                            <td style={styles.td}>{artist.country || '—'}</td>
                                            <td style={styles.td}>
                                                <span style={styles.showCount}>{artist.appearances}x</span>
                                            </td>
                                            {isSeries && (
                                                <td style={styles.td}>
                                                    <span style={styles.showCount}>{artist.festival_count}x</span>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Venues Tab (body tab) ────────────────────────────────────────────────────
// Mirrors ArtistsHostedTab exactly, for venues instead of artists. `venues`
// is already scoped server-side (own events for a standalone/child festival,
// pooled across child festivals for a series) — no branching needed here.
function VenuesTab({ venues }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();

    if (!venues || venues.length === 0) return <div style={styles.emptyTab}>No venue data recorded yet.</div>;

    const q = normalizeStr(search);
    const filtered = search ? venues.filter(v =>
        [v.name, v.city, v.state, v.country]
            .some(val => val && normalizeStr(val).includes(q))
    ) : venues;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by venue, city, state, country..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No venues match your search.</div>
            ) : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(venue => (
                                <RelationshipCard
                                    key={venue.id}
                                    title={venue.name}
                                    titleTo={`/venues/${venue.slug || venue.id}`}
                                    titleMeta={capacityLabel(venue.capacity)}
                                    lines={[joinLocation(venue.city, venue.state, venue.country)]}
                                    counts={[{ value: `${venue.event_count}x`, label: 'Events' }]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Venue</th>
                                        <th style={styles.th}>City</th>
                                        <th style={styles.th}>State/Region</th>
                                        <th style={styles.th}>Country</th>
                                        <th style={styles.th}>Capacity</th>
                                        <th style={styles.th}>Events</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((venue, idx) => (
                                        <tr key={venue.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}>
                                                <Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link>
                                            </td>
                                            <td style={styles.td}>{venue.city || '—'}</td>
                                            <td style={styles.td}>{venue.state || '—'}</td>
                                            <td style={styles.td}>{venue.country || '—'}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{venue.capacity != null ? Number(venue.capacity).toLocaleString() : '—'}</td>
                                            <td style={styles.td}>
                                                <span style={styles.showCount}>{venue.event_count}x</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Show Archive Tab (body tab — Section 4 of the redesign spec) ───────────────
// Same search + columns as the pre-redesign "Events" tab. `events` is already
// scoped server-side (own events for standalone/child, pooled child-festival
// events for a series) — no branching needed here. Search and page are local
// state so switching away and back to this tab (or to a different festival,
// via the `key={festival.id}` on the parent) resets cleanly.
function ShowArchiveTab({ events, attendedMap, performedMap, workedMap, togglingMap, onToggleAttended }) {
    const isMobile = useIsMobile();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    if (events.length === 0) return <div style={styles.emptyTab}>No events linked to this festival yet.</div>;

    const q = normalizeStr(search);
    const filtered = search
        ? events.filter(e => [
            e.event_date ? formatDateLong(e.event_date) : '',
            e.venue_name, e.city, e.state, e.country, e.artists
          ].some(v => v && normalizeStr(v).includes(q)))
        : events;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, venue, city, artists..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No events match your search.</div>
            ) : (
                <>
                    {isMobile ? (
                        <ShowArchiveCardList>
                            {paginated.map(e => {
                                const isAttended = attendedMap[e.event_id] === true;
                                const isPerformed = performedMap[e.event_id] === true;
                                const isWorked = workedMap[e.event_id] === true;
                                const isToggling = togglingMap[e.event_id];
                                return (
                                    <ShowArchiveCard
                                        key={e.event_id}
                                        title={formatShowDateLong(e.event_date) || '—'}
                                        titleTo={`/events/${e.event_slug || e.event_id}`}
                                        venueName={e.venue_name}
                                        venueTo={`/venues/${e.venue_slug || e.venue_id}`}
                                        location={joinLocation(e.city, e.state, e.country)}
                                        lineup={e.artists}
                                        footer={isPerformed ? <span style={{ ...styles.performedBadge, ...cardControl }}>Performed</span>
                                        : isWorked ? <span style={{ ...styles.workedBadge, ...cardControl }}>Worked</span>
                                        : <button onClick={() => onToggleAttended(e.event_id)} disabled={isToggling} style={{ ...(isAttended ? styles.attendedBtnOn : styles.attendedBtnOff), ...cardControl }}>{isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}</button>}
                                    />
                                );
                            })}
                        </ShowArchiveCardList>
                    ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Artists</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((e, idx) => {
                                    const formattedDate = formatDateShort(e.event_date) || '—';
                                    const isAttended = attendedMap[e.event_id] === true;
                                    const isPerformed = performedMap[e.event_id] === true;
                                    const isWorked = workedMap[e.event_id] === true;
                                    const isToggling = togglingMap[e.event_id];
                                    return (
                                        <tr key={e.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}>
                                                <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                    {formattedDate}
                                                </Link>
                                            </td>
                                            <td style={styles.td}>{e.city || '—'}</td>
                                            <td style={styles.td}>{e.state || '—'}</td>
                                            <td style={styles.td}>{e.country || '—'}</td>
                                            <td style={styles.td}>
                                                <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                    {e.venue_name}
                                                </Link>
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {e.artists || '—'}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isPerformed ? (
                                                    <span style={styles.performedBadge}>Performed</span>
                                                ) : isWorked ? (
                                                    <span style={styles.workedBadge}>Worked</span>
                                                ) : (
                                                    <button
                                                        onClick={() => onToggleAttended(e.event_id)}
                                                        disabled={isToggling}
                                                        style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                    >
                                                        {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Child Festivals Tab (body tab — series only) ────────────────────────────
// Unchanged expand-in-place behavior; only addition is pagination over the
// top-level child festival rows (an expanded child's own event rows are not
// separately paginated — those lists are typically small).
function ChildFestivalsTab({ childFestivals, childFestivalEvents }) {
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState(null);

    if (childFestivals.length === 0) return <div style={styles.emptyTab}>No child festivals linked to this series yet.</div>;

    const paginated = childFestivals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>Festival</th>
                            <th style={styles.th}>Dates</th>
                            <th style={styles.th}>Events</th>
                            <th style={styles.th}># of Artists</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.map((cf, idx) => {
                            const isExpanded = expanded === cf.id;
                            const cfEvents = (childFestivalEvents || []).filter(e => e.festival_id === cf.id);
                            const cfStart = formatDateShort(cf.start_date);
                            const cfEnd = formatDateShort(cf.end_date);
                            const cfDateRange = cfStart && cfEnd && cfStart !== cfEnd
                                ? `${cfStart} – ${cfEnd}`
                                : cfStart || '—';
                            return (
                                <React.Fragment key={cf.id}>
                                    <tr
                                        style={{ ...(idx % 2 === 0 ? styles.trEven : styles.trOdd), cursor: cfEvents.length > 0 ? 'pointer' : 'default' }}
                                        onClick={() => cfEvents.length > 0 && setExpanded(isExpanded ? null : cf.id)}
                                    >
                                        <td style={{ ...styles.td, width: '28px', color: '#bb86fc', fontSize: '12px' }}>
                                            {cfEvents.length > 0 ? (isExpanded ? '▾' : '▸') : ''}
                                        </td>
                                        <td style={styles.td}>
                                            <Link to={`/festivals/${cf.slug || cf.id}`} style={styles.tableLink} onClick={e => e.stopPropagation()}>
                                                {cf.name}
                                            </Link>
                                        </td>
                                        <td style={styles.td}>{cfDateRange}</td>
                                        <td style={styles.td}>
                                            <span style={styles.badge}>{cf.event_count}</span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.badge}>{cf.artist_count}</span>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '0', backgroundColor: '#131313', borderBottom: '1px solid #333' }}>
                                                <table style={{ ...styles.table, margin: 0 }}>
                                                    <thead>
                                                        <tr style={{ backgroundColor: '#1a1a1a' }}>
                                                            <th style={{ ...styles.th, paddingLeft: '40px' }}>Date</th>
                                                            <th style={styles.th}>City</th>
                                                            <th style={styles.th}>State</th>
                                                            <th style={styles.th}>Country</th>
                                                            <th style={styles.th}>Venue</th>
                                                            <th style={styles.th}>Artists</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cfEvents.map((e, ei) => (
                                                            <tr key={e.event_id} style={{ backgroundColor: ei % 2 === 0 ? '#161616' : '#131313' }}>
                                                                <td style={{ ...styles.td, paddingLeft: '40px' }}>
                                                                    <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                        {formatDateShort(e.event_date) || '—'}
                                                                    </Link>
                                                                </td>
                                                                <td style={styles.td}>{e.city || '—'}</td>
                                                                <td style={styles.td}>{e.state || '—'}</td>
                                                                <td style={styles.td}>{e.country || '—'}</td>
                                                                <td style={styles.td}>
                                                                    <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                                        {e.venue_name}
                                                                    </Link>
                                                                </td>
                                                                <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{e.artists || '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <Pagination currentPage={page} totalItems={childFestivals.length} onPageChange={setPage} />
        </div>
    );
}

function FestivalDetail({ slugOverride } = {}) {
    const isMobile = useIsMobile();
    const params = useParams();
    const navigate = useNavigate();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeInfoTab, setActiveInfoTab] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);
    const [defaultImageResolving, setDefaultImageResolving] = useState(false);

    // Attended state
    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (eventList) => {
        if (!eventList || eventList.length === 0) return;
        try {
            const eventIds = eventList.map(e => e.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const attendedMap2 = {};
            const performedMap2 = {};
            const workedMap2 = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                attendedMap2[id] = status.attended;
                performedMap2[id] = status.performed || false;
                workedMap2[id] = status.worked || false;
            });
            setAttendedMap(attendedMap2);
            setPerformedMap(performedMap2);
            setWorkedMap(workedMap2);
        } catch (e) { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    const handleDefaultChanged = useCallback((imageId, imageUrl) => {
        setDefaultImageId(imageId);
        setDefaultImageUrl(imageUrl);
    }, []);

    useEffect(() => {
        // Client-side navigation between two festival pages (e.g. clicking a
        // child festival from a series' Child Festivals tab) reuses this same
        // component instance rather than remounting it — slug changes, but
        // React-state like activeTab doesn't reset on its own. Without this,
        // a tab index that doesn't exist on the new festival renders nothing,
        // which reads as "events missing" until a hard refresh. (Per-tab
        // search/page state lives inside ShowArchiveTab/ChildFestivalsTab/
        // ArtistsHostedTab now, and resets via their `key={festival.id}`.)
        setActiveInfoTab(0);
        setActiveTab(0);
        setShowDeleteModal(false);

        async function load() {
            try {
                const res = await axios.get(`${config.API_URL}/festivals/${slug}`);
                setData(res.data);
                setLoading(false);
                setDefaultImageId(null);
                setDefaultImageUrl(null);
                setDefaultImageResolving(false);
                if (res.data?.festival?.default_image_id) {
                    setDefaultImageId(res.data.festival.default_image_id);
                    setDefaultImageResolving(true);
                    try {
                        const token = await getIdToken();
                        const imgRes = await axios.get(`${config.API_URL}/images`, {
                            params: { entityType: 'festival', entityId: res.data.festival.id },
                            headers: { Authorization: token },
                        });
                        const imgs = imgRes.data.images || [];
                        const defaultImg = imgs.find(i => i.id === res.data.festival.default_image_id);
                        if (defaultImg) setDefaultImageUrl(defaultImg.image_url);
                    } catch { /* fall back to festival.image_url */ }
                    finally { setDefaultImageResolving(false); }
                }
                if (res.data && res.data.festival) {
                    document.title = `SHARC - ${res.data.festival.name}`;
                }
                if (res.data && res.data.events) {
                    loadAttendedStatus(res.data.events);
                }
            } catch {
                setError('Failed to load festival');
                setLoading(false);
            }
        }
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    if (loading) return <div style={styles.message}>Loading festival...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const {
        festival, events, promoters, links, location, capacity, stats, artists_hosted, venues,
        has_existing_events, pending_deletion, parent_festival, child_festivals, child_festival_events
    } = data;

    // Body-tab layout: Child Festivals only exists for a series, which shifts
    // every tab after it. Named indices instead of magic numbers so the tab
    // content below stays readable regardless of which shape is active.
    const TABS = festival.is_parent
        ? ['Show Archive', 'Child Festivals', 'Venues', 'Artists Hosted', 'Flyers', 'Photos']
        : ['Show Archive', 'Venues', 'Artists Hosted', 'Flyers', 'Photos'];
    const CHILD_FESTIVALS_TAB = festival.is_parent ? 1 : -1;
    const VENUES_TAB = festival.is_parent ? 2 : 1;
    const ARTISTS_HOSTED_TAB = festival.is_parent ? 3 : 2;
    const FLYERS_TAB = festival.is_parent ? 4 : 3;
    const PHOTOS_TAB = festival.is_parent ? 5 : 4;

    // (2026-10-22) Last resort: getFestival's fallback_image_url — one of this
    // festival's own multi-event images (Festival Flyer History), only sent
    // when the festival has no default image and no legacy image_url.
    const heroImage = defaultImageUrl || festival.image_url || festival.fallback_image_url;

    // Attended = Attended, Performed, or Worked any show at this festival
    // (SHARC_Spec_Festival_Browse_2026-10-07, §5). Same approach as
    // VenueDetail.js's Visited badge: computed from the status maps Show
    // Archive already loads, so it updates live when the user toggles
    // Attended there, and the maps stay empty for logged-out visitors so the
    // badge never renders for them. Never shown on a series.
    const hasAttended = !festival.is_parent &&
        (events || []).some(e => attendedMap[e.event_id] || performedMap[e.event_id] || workedMap[e.event_id]);

    // Capacity range (header-left) — spec §2.5: a single value when
    // min === max, otherwise "min - max". Hidden entirely when no linked
    // event resolves to a capacity.
    let capacityLabel = null;
    if (capacity && capacity.min != null && capacity.max != null) {
        const min = Number(capacity.min);
        const max = Number(capacity.max);
        capacityLabel = min === max
            ? `Capacity: ${min.toLocaleString()}`
            : `Capacity: ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }

    // Term of shows (Show Stats tab) — identical calculation to VenueDetail.js.
    let termStr = '—';
    if (stats.first_show_date && stats.last_show_date) {
        let d1 = new Date(stats.first_show_date); let d2 = new Date(stats.last_show_date);
        let years = d2.getUTCFullYear() - d1.getUTCFullYear();
        let months = d2.getUTCMonth() - d1.getUTCMonth();
        let days = d2.getUTCDate() - d1.getUTCDate();
        if (days < 0) { months -= 1; days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate(); }
        if (months < 0) { years -= 1; months += 12; }
        const parts = [];
        if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
        if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
        if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
        termStr = parts.join(' ') || '—';
    }

    return (
        <div style={{ width: '100%', minWidth: 0 }}>
            {/* ── Section 1: Festival Name + Attended badge + Actions dropdown ── */}
            <div style={styles.section1}>
                <h1 style={styles.festivalNameHeading}>{festival.name}{festival.is_parent ? ' (Series)' : ''}</h1>
                <div style={styles.section1Right}>
                    {hasAttended && (
                        <span style={styles.attendedBadge}>✓ Attended</span>
                    )}
                    {!pending_deletion && (
                        <ActionsDropdown
                            editPath={`/festivals/${festival.slug || festival.id}/edit`}
                            editLabel="Edit Festival"
                            onDeleteClick={() => setShowDeleteModal(true)}
                            deleteLabel="Delete Festival"
                            deleteDisabled={has_existing_events}
                            deleteTooltip="This festival cannot be deleted while it is tied to an existing event. Remove it from all events first."
                        />
                    )}
                </div>
            </div>

            {pending_deletion && (
                <div style={styles.pendingDeletionBanner}>
                    <span style={styles.pendingDeletionIcon}>🗑️</span>
                    <span>
                        Festival marked for deletion by <strong>{pending_deletion.requested_by}</strong> on{' '}
                        <strong>{formatRequestedAt(pending_deletion.requested_at)}</strong>
                        {pending_deletion.reason && (
                            <> — <em>{pending_deletion.reason}</em></>
                        )}
                    </span>
                </div>
            )}

            {showDeleteModal && (
                <DeletionRequestModal
                    entityType="festival"
                    entityId={festival.id}
                    entityName={festival.name}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={() => navigate('/festivals')}
                />
            )}

            {/* ── Section 2: Image/meta left + info tabs right ── */}
            <div style={isMobile ? { ...styles.headerGrid, ...Smobile.headerGrid } : styles.headerGrid}>
                {/* Left: image + derived location + series/promoters/capacity */}
                <div style={styles.headerLeft}>
                    <div style={styles.headerImageRow}>
                        {!defaultImageResolving && heroImage ? (
                            <img
                                src={heroImage}
                                alt={festival.name}
                                style={styles.festivalImage}
                                onError={e => { e.target.outerHTML = `<div style="width:160px;height:160px;border-radius:8px;border:1px solid #333;background:#1e1e1e;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;flex-shrink:0"><div style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">No Image</div></div>`; }}
                            />
                        ) : (
                            <div style={styles.noImageBox}>
                                <div style={styles.noImageLabel}>{defaultImageResolving ? '…' : 'No Image'}</div>
                            </div>
                        )}
                        <div style={styles.headerMeta}>
                            {location && <div style={styles.location}>{location}</div>}
                            {parent_festival && (
                                <div style={styles.tag}>
                                    Part of <Link to={`/festivals/${parent_festival.slug || parent_festival.id}`} style={styles.tagLink}>{parent_festival.name}</Link>
                                </div>
                            )}
                            {promoters && promoters.length > 0 && (
                                <div style={styles.tag}>
                                    {promoters.map((p, i) => (
                                        <React.Fragment key={p.id}>
                                            {i > 0 && <span style={{ color: '#888', margin: '0 4px' }}>·</span>}
                                            <Link to={`/promoters/${p.slug}`} style={styles.tagLink}>{p.name}</Link>
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                            {capacityLabel && (
                                <span style={styles.tag}>{capacityLabel}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: info tabs */}
                <div style={styles.headerRight}>
                    {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                    {isMobile ? (
                        <MobileTabSelect tabs={INFO_TABS} active={activeInfoTab} onChange={setActiveInfoTab} />
                    ) : (
                        <div style={styles.infoTabBar}>
                            {INFO_TABS.map((tab, i) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveInfoTab(i)}
                                    style={{ ...styles.infoTabBtn, ...(activeInfoTab === i ? styles.infoTabBtnActive : {}) }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}
                    <div style={styles.infoTabContent}>
                        {/* Show Stats */}
                        {activeInfoTab === 0 && (
                            <div style={isMobile ? { ...styles.showStatsLayout, ...Smobile.showStatsLayout } : styles.showStatsLayout}>
                                <div style={styles.totalShowsBox}>
                                    <div style={styles.statValue}>{stats.total_shows || 0}</div>
                                    <div style={styles.statLabel}>Total Shows</div>
                                </div>
                                <div style={styles.totalShowsBox}>
                                    <div style={styles.statValue}>{stats.total_artists || 0}</div>
                                    <div style={styles.statLabel}>Total Artists</div>
                                </div>
                                <table style={styles.statsTable}>
                                    <tbody>
                                        <tr>
                                            <td style={styles.statsTableLabel}>First Show</td>
                                            <td style={styles.statsTableValue}>{formatDate(stats.first_show_date)}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.statsTableLabel}>Most Recent Show</td>
                                            <td style={styles.statsTableValue}>{formatDate(stats.last_show_date)}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.statsTableLabel}>Term of Shows</td>
                                            <td style={styles.statsTableValue}>{termStr}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Bio */}
                        {activeInfoTab === 1 && (
                            <div>
                                {festival.description
                                    ? <div style={styles.bioText}>{festival.description}</div>
                                    : <div style={styles.emptyInfo}>No bio available.</div>}
                            </div>
                        )}

                        {/* Links */}
                        {activeInfoTab === 2 && (
                            <div>
                                {links && links.length > 0
                                    ? <div style={styles.linksGrid}>
                                        {links.map(link => (
                                            <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" style={styles.linkChip}>
                                                {link.description} ↗
                                            </a>
                                        ))}
                                    </div>
                                    : <div style={styles.emptyInfo}>No links added yet.</div>}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Section 3: Body tabs ── */}
            <div style={styles.tabsWrapper}>
                {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                {isMobile ? (
                    <MobileTabSelect tabs={TABS} active={activeTab} onChange={setActiveTab} />
                ) : (
                    <div style={styles.tabBar}>
                        {TABS.map((tab, i) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(i)}
                                style={{
                                    ...styles.tabBtn,
                                    ...(activeTab === i ? styles.tabBtnActive : {}),
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                )}

                <div style={styles.tabContent}>
                    {/* ── Show Archive tab ── */}
                    {activeTab === 0 && (
                        <ShowArchiveTab
                            key={festival.id}
                            events={events}
                            attendedMap={attendedMap}
                            performedMap={performedMap}
                            workedMap={workedMap}
                            togglingMap={togglingMap}
                            onToggleAttended={handleToggleAttended}
                        />
                    )}

                    {/* ── Child Festivals tab (series only) ── */}
                    {festival.is_parent && activeTab === CHILD_FESTIVALS_TAB && (
                        <ChildFestivalsTab
                            key={festival.id}
                            childFestivals={child_festivals}
                            childFestivalEvents={child_festival_events}
                        />
                    )}

                    {/* ── Venues tab ── */}
                    {activeTab === VENUES_TAB && (
                        <VenuesTab key={festival.id} venues={venues} />
                    )}

                    {/* ── Artists Hosted tab ── */}
                    {activeTab === ARTISTS_HOSTED_TAB && (
                        <ArtistsHostedTab key={festival.id} artists={artists_hosted} isSeries={festival.is_parent} />
                    )}

                    {/* ── Flyers tab ── */}
                    {activeTab === FLYERS_TAB && (
                        festival.is_parent ? (
                            <SeriesFlyerHistoryTab key={festival.id} childFestivals={child_festivals} />
                        ) : (
                            <FlyerHistoryTabs
                                entityType="festival"
                                entityId={festival.id}
                                shows={events}
                                searchByOptions={['Artist', 'Venue']}
                                multiEventTabLabel="Festival Flyer History"
                                showFlyerHistoryLabel="Show Flyer History"
                            />
                        )
                    )}

                    {/* ── Photos tab (last position, 2026-10-15) ── */}
                    {activeTab === PHOTOS_TAB && (
                        <PhotosTab
                            festivalId={festival.id}
                            festivalName={festival.name}
                            defaultImageId={defaultImageId}
                            onDefaultChanged={handleDefaultChanged}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    // ── Section 1 ──
    section1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' },
    festivalNameHeading: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0, lineHeight: 1.2 },
    // Copied from VenueDetail.js's section1Right / visitedBadge (itself a copy
    // of ArtistDetail.js's seenBadge) so all three badges match exactly.
    section1Right: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
    attendedBadge: { display: 'inline-block', backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'default' },
    pendingDeletionBanner: {
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        backgroundColor: '#2c1818', border: '1px solid #e57373', borderRadius: '6px',
        padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#e0a0a0', lineHeight: 1.5
    },
    pendingDeletionIcon: { fontSize: '16px', lineHeight: 1.4, flexShrink: 0 },

    // ── Section 2 header grid ──
    headerGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '32px', alignItems: 'start' },
    headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
    headerImageRow: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    festivalImage: { width: '160px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    noImageBox: { width: '160px', height: '160px', borderRadius: '8px', border: '1px solid #333', background: '#1e1e1e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flexShrink: 0 },
    noImageLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
    headerMeta: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px' },
    location: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },
    tag: { display: 'inline-block', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },
    tagLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },

    // ── Info tabs (right side of section 2) ──
    headerRight: { minWidth: 0 },
    // Tab bars (2026-09-23 scroll fix, same as MyShows.js): overflowX: 'auto'
    // forces overflow-y to 'auto' too, and the old negative-marginBottom tab
    // buttons overflowed the bar by 1–2px, so a mouse wheel over the bar
    // scrolled the bar itself instead of the page. Divider is now an inset
    // box-shadow (painted beneath the buttons, so the active underline covers
    // it with zero overflow) plus an explicit overflowY: 'hidden'.
    infoTabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', gap: '2px', overflowX: 'auto', overflowY: 'hidden' },
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', minHeight: '160px', maxHeight: '280px', overflowY: 'auto' },
    bioText: { fontSize: '14px', color: '#ccc', lineHeight: '1.7', maxHeight: '96px', overflowY: 'auto' },

    // ── Show Stats tab ──
    showStatsLayout: { display: 'flex', gap: '24px', alignItems: 'center', height: '100%' },
    totalShowsBox: { flexShrink: 0, backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px 28px', textAlign: 'center', minWidth: '110px' },
    statsTable: { flex: 1, borderCollapse: 'collapse', fontSize: '14px' },
    statsTableLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%' },
    statsTableValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a' },
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },

    // ── Links / info ──
    linksGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    linkChip: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    emptyInfo: { color: '#555', fontSize: '14px', padding: '20px 0', textAlign: 'center' },

    // ── Photos ──
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoCardDefault: { borderColor: '#bb86fc' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    isDefaultLabel: { fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' },
    addPhotoBtn: { background: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },

    // ── Section 3: Body tabs ──
    tabsWrapper: { marginTop: '8px', width: '100%', minWidth: 0 },
    tabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px', width: '100%', minWidth: 0, boxSizing: 'border-box' },

    // ── Tables ──
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    badge: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    // ── Pagination (jump-to-page, screenshot reference: Adidas.com) ──────────
    pageJumpWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

// ── Mobile style overrides (spec: SHARC_FestivalDetail_Redesign_Spec_2026-09-10) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true — same pattern VenueDetail.js/FestivalDetail.js
// already use site-wide.
const Smobile = {
    // Image/meta (left) + info tabs (right) — stack instead of a fixed
    // 1fr/2fr grid.
    headerGrid: {
        gridTemplateColumns: '1fr',
        gap: '20px',
    },
    // "Total Shows" box + stats table (in the Show Stats tab) — stack
    // instead of a flex row, so the stats table isn't squeezed into ~200px.
    showStatsLayout: {
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 'auto',
    },
    // (2026-10-28) The body tab bar's tabBar/tabBtn overrides were removed:
    // on mobile the tab row is now a MobileTabSelect dropdown.
};

export default FestivalDetail;
